import { type FC, useState } from "react";
import { Col, Row } from "~/common/components/Layout";

import { CloseBtn } from "~/features/addToCart/components/AtcCloseButton";

import Modal from "~/common/components/Modal";
import { type SavedConfiguration } from "~/features/configurator/Linear/models";
import { useForm } from "react-hook-form";
import ShareConfigurationsViaEmailPopupForm from "~/features/savedConfigurations/components/ShareConfigurationsViaEmailPopupForm";
import { sendLinearConfigurationsViaEmail } from "~/features/configurator/Linear/api/ShareConfigurationsViaEmail";
import FullscreenLoader from "~/features/loading/FullscreenLoader";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { submitAsForm } from "~/common/utils/ApiHelper";
import { Button, Stack } from "~/common/mui";
import { useTranslation } from "react-i18next";

interface Props {
  onClose: () => void;
  items: SavedConfiguration[];
  onSuccess: () => void;
}

const ShareConfigurationsViaEmailPopup: FC<Props> = ({ onClose, items, onSuccess }) => {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm();
  const [isFormLoading, setIsFormLoading] = useState(false);

  const onSubmitHandler = handleSubmit((values) => {
    const sharedConfigurations = items.map((item) => item.code);
    const email = values.email;
    const consent = values.consent;
    if (email && consent) {
      setIsFormLoading(true);
      sendLinearConfigurationsViaEmail({
        sharedConfigurations,
        email: email,
        firstName: values.firstName,
        lastName: values.lastName,
        note: values.note,
      })
        .then((result) => {
          if (result.error) {
            setError("email", { message: result.error });
          } else {
            const confirmationUrl = resolveApiUrl("/sendLinearConfigurationsViaEmail/confirmation");
            const confirmationData = {
              configurations: JSON.stringify(sharedConfigurations),
            };
            submitAsForm(confirmationData, confirmationUrl);
          }
        })
        .catch(() => setIsFormLoading(false));
    }
  });

  if (isFormLoading) {
    return <FullscreenLoader />;
  }

  return (
    <Modal
      onClose={onClose}
      title={t("page.savedConfigsPopup.shareViaEmail.configurations", { count: items.length })}
      footerChildren={
        <Row className={"d-flex flex-justify-between flex-align-center w-100"}>
          <p>{t("mandatory")}</p>
          <Stack direction={"row"} gap={1}>
            <CloseBtn onClick={onClose}>{t("cancel")}</CloseBtn>
            <Button onClick={onSubmitHandler}>{t("page.savedConfigsPopup.shareViaEmail.send")}</Button>
          </Stack>
        </Row>
      }
    >
      <Row>
        <Col size={12}>
          <ShareConfigurationsViaEmailPopupForm register={register} control={control} errors={errors} />
        </Col>
      </Row>
    </Modal>
  );
};

export default ShareConfigurationsViaEmailPopup;
